import React from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Grid,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { modalStyle, headerStyle, inputStyle } from "../OrderEntry/styles";
import { tokens, withAlpha } from "../../Common/designTokens";
import { FIELD_TYPES, formatDisplayValue } from "./updateSecurityConfig";
import { useUpdateSecurity } from "./useUpdateSecurity";
import { FloatInput } from "../OrderEntry/NumericInputs";
import IssuerAutocomplete from "./IssuerAutocomplete";

const UpdateSecurityPopup = ({ open, onClose, securityData, onUpdated, fieldOptions = {} }) => {
  const {
    formValues,
    errors,
    submitError,
    loading,
    editableFields,
    disabledFields,
    hasChanges,
    handleChange,
    handleSubmit,
  } = useUpdateSecurity({ securityData, open, onClose, onUpdated });

  const PINNED_KEYS = ["identifier", "price", "poolFactor"];
  const editableKeySet = new Set(editableFields.map((f) => f.key));

  const renderSelectField = (field) => {
    const options = fieldOptions[field.optionsKey] || [];
    const currentVal = formValues[field.key] ?? "";
    return (
      <Grid item xs={2.4} key={field.key}>
        <FormControl fullWidth variant="filled" error={Boolean(errors[field.key])} sx={inputStyle}>
          <InputLabel>{field.label}</InputLabel>
          <Select
            name={field.key}
            value={currentVal}
            onChange={(e) => handleChange(field.key, e.target.value)}
            disableUnderline
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            <MenuItem value=""><em>Select</em></MenuItem>
            {options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
          {errors[field.key] && <FormHelperText>{errors[field.key]}</FormHelperText>}
        </FormControl>
      </Grid>
    );
  };

  const renderDateField = (field) => {
    const stored = formValues[field.key] ?? "";
    const dayjsValue = stored ? dayjs(stored, "YYYY-MM-DD") : null;
    return (
      <Grid item xs={2.4} key={field.key}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={field.label}
            value={dayjsValue}
            onChange={(newVal) => {
              if (!newVal || !newVal.isValid()) {
                handleChange(field.key, "");
                return;
              }
              handleChange(field.key, newVal.format("YYYY-MM-DD"));
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "filled",
                error: Boolean(errors[field.key]),
                helperText: errors[field.key] || undefined,
                sx: inputStyle,
                InputProps: { disableUnderline: true },
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
    );
  };

  const renderIssuerField = (field) => (
    <Grid item xs={2.4} key={field.key}>
      <IssuerAutocomplete
        label={field.label}
        value={formValues[field.key] ?? ""}
        onChange={(val) => handleChange(field.key, val)}
        options={fieldOptions[field.optionsKey] || []}
        error={Boolean(errors[field.key])}
        helperText={errors[field.key]}
      />
    </Grid>
  );

  const renderEditableField = (field) => {
    if (field.key === "issuer") return renderIssuerField(field);
    if (field.type === FIELD_TYPES.SELECT) return renderSelectField(field);
    if (field.type === FIELD_TYPES.DATE) return renderDateField(field);

    if (field.type === FIELD_TYPES.NUMBER) {
      return (
        <Grid item xs={2.4} key={field.key}>
          <FloatInput
            name={field.key}
            label={field.label}
            value={formValues[field.key] ?? ""}
            onChange={(e) => handleChange(field.key, e.target.value)}
            error={Boolean(errors[field.key])}
            decimalScale={field.maxDecimals ?? 4}
            maxValue={field.max}
            allowNegative={false}
          />
          {errors[field.key] && (
            <Box sx={{ color: "error.main", fontSize: "12px", mt: 0.5, px: 1 }}>
              {errors[field.key]}
            </Box>
          )}
        </Grid>
      );
    }

    return (
      <Grid item xs={2.4} key={field.key}>
        <TextField
          fullWidth
          variant="filled"
          label={field.label}
          name={field.key}
          value={formValues[field.key] ?? ""}
          onChange={(event) => handleChange(field.key, event.target.value)}
          error={Boolean(errors[field.key])}
          helperText={errors[field.key] || undefined}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
          inputProps={{
            ...(field.maxLength ? { maxLength: field.maxLength } : {}),
          }}
        />
      </Grid>
    );
  };

  const renderDisabledField = (field) => (
    <Grid item xs={2.4} key={field.key}>
      <TextField
        fullWidth
        disabled
        variant="filled"
        label={field.label}
        value={formatDisplayValue(field, securityData?.[field.key])}
        sx={inputStyle}
        InputProps={{ disableUnderline: true }}
      />
    </Grid>
  );

  const renderField = (field) =>
    editableKeySet.has(field.key) ? renderEditableField(field) : renderDisabledField(field);

  const pinnedFields = PINNED_KEYS
    .map((key) => [...editableFields, ...disabledFields].find((f) => f.key === key))
    .filter(Boolean);
  const remainingEditable = editableFields.filter((f) => !PINNED_KEYS.includes(f.key));
  const remainingDisabled = disabledFields.filter((f) => !PINNED_KEYS.includes(f.key));

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      disableEscapeKeyDown
      aria-labelledby="update-security-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          display: "flex",
          flexDirection: "column",
          maxHeight: "95vh",
          width: "82vw",
          maxWidth: "90vw",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ ...headerStyle, flexShrink: 0 }}>
          <Typography
            id="update-security-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: "bold" }}
          >
            Update Security
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: tokens.colors.fontColour }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Body — pinned (identifier/price/poolFactor) first, then editable, then disabled */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <Grid container spacing={2}>
            {pinnedFields.map(renderField)}
            {remainingEditable.map(renderEditableField)}
            {remainingDisabled.map(renderDisabledField)}
          </Grid>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            p: 2,
            borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
            backgroundColor: tokens.colors.defaultGridRowColor,
          }}
        >
          {submitError && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                backgroundColor: withAlpha(tokens.palette.danger, 0.133),
                border: `1px solid ${tokens.palette.danger}`,
                borderRadius: "8px",
              }}
            >
              <Typography sx={{ color: tokens.palette.danger, fontSize: "14px", fontWeight: 500 }}>
                {submitError}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 4,
                py: 1,
                width: "150px",
                height: "46px",
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading || !hasChanges}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 4,
                py: 1,
                width: "155px",
                height: "46px",
                borderRadius: "8px",
                backgroundColor: tokens.palette.primary,
                "&:hover": { backgroundColor: tokens.palette.primaryA80 },
                "&.Mui-disabled": {
                  backgroundColor: withAlpha(tokens.palette.primary, 0.45),
                  color: tokens.colors.white,
                  opacity: 1,
                  cursor: "not-allowed",
                },
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UpdateSecurityPopup;
